import React, { useMemo } from 'react';
import {
  styled,
  alpha,
  FormControlLabel,
  FormHelperText,
  SxProps,
  Box,
  Popover,
} from '@mui/material';
import { ArrowDropDown, Close as NoValueIcon } from '@mui/icons-material';
import { Field, useField, useForm } from 'react-final-form';
import { anchorRef, bindTrigger, usePopupState, bindPopover } from 'material-ui-popup-state/hooks';
import { SketchPicker, ColorResult } from 'react-color';

import { useCanEditFormFieldsContext } from '@occc-app/App/CanEditFormFieldsContext';

const ColorPickerWrapper = styled('div', {
  shouldForwardProp: (propName: string) => !['color'].includes(propName),
})<{ color: string }>((props) => {
  const bgColor = '#eaeaea';
  return {
    position: 'relative',
    boxSizing: 'content-box',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: 44,
    height: 22,
    backgroundColor: bgColor,
    borderRadius: 20,
    transition: 'background-color 300ms',
    border: `4px solid ${bgColor}`,
    '&:hover': {
      backgroundColor: alpha(props.color, 0.3),
    },
  };
});

const ColorPickerIndicator = styled('div', {
  shouldForwardProp: (propName: string) => !['color'].includes(propName),
})<{ color: string }>((props) => ({
  width: 22,
  height: 22,
  borderRadius: '50%',
  backgroundColor: props.color,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const DEFAULT_COLOR = '#ffffff' as const;
const ACTION_ICON_COLOR = '#767676' as const;

type ColorPickerProps = {
  name: string;
  sx?: SxProps;
};

export const ColorPicker: React.FC<ColorPickerProps> = (props) => {
  const filterPopupState = usePopupState({ variant: 'popover', popupId: 'filters' });
  const { name, sx } = props;
  const form = useForm();
  const { isEditable } = useCanEditFormFieldsContext();

  const clearColorButton = useMemo(
    () => (
      <NoValueIcon
        onClick={(event: React.MouseEvent<HTMLOrSVGElement>) => {
          event.stopPropagation();
          form.change(name, null);
        }}
        sx={{ color: ACTION_ICON_COLOR }}
        fontSize='small'
      />
    ),
    [form, name],
  );

  return (
    <Field
      name={name}
      render={({ input }) => {
        const color = input.value || DEFAULT_COLOR;
        const disabled = input.disabled || !isEditable;
        const isColorSet = input.value;

        return (
          <Box
            sx={{
              cursor: disabled ? 'not-allowed' : 'pointer',
            }}
          >
            <ColorPickerWrapper
              color={color}
              sx={{
                opacity: disabled ? 0.5 : 1,
                pointerEvents: disabled ? 'none' : 'auto',
                ...sx,
              }}
              {...bindTrigger(filterPopupState)}
              ref={disabled ? null : anchorRef(filterPopupState)}
            >
              <ColorPickerIndicator color={color}>
                {!isColorSet && clearColorButton}
              </ColorPickerIndicator>
              {isColorSet ? (
                clearColorButton
              ) : (
                <ArrowDropDown sx={{ color: ACTION_ICON_COLOR }} fontSize='small' />
              )}
            </ColorPickerWrapper>
            <Popover
              {...bindPopover(filterPopupState)}
              anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
            >
              <SketchPicker
                disableAlpha
                color={color}
                onChange={(color: ColorResult) => {
                  form.change(name, color.hex);
                }}
              />
            </Popover>
          </Box>
        );
      }}
    />
  );
};

type ColorPickerWithLabelProps = {
  name: string;
  label?: string;
};

const ColorPickerHelperText = ({ name }: { name: string }) => {
  const { meta } = useField(name, {
    subscription: {
      error: true,
      submitError: true,
      touched: true,
      modified: true,
      submitFailed: true,
    },
  });
  const isError =
    !!(meta.error || meta.submitError) && (meta.touched || meta.modified || meta.submitFailed);

  if (!isError) {
    return null;
  }

  return <FormHelperText error>{meta.error || meta.submitError}</FormHelperText>;
};

export const ColorPickerWithLabel = (props: ColorPickerWithLabelProps) => {
  const { name, label } = props;
  return (
    <Box>
      <FormControlLabel control={<ColorPicker name={name} sx={{ mr: 1 }} />} label={label} />
      <ColorPickerHelperText name={name} />
    </Box>
  );
};
